import { displayDate, displayDateTimeCSV } from "@common/utils/localTime.utils";
import Grid from '@mui/material/Grid2';
import { MappedDevice } from "@pages/Assets/Devices/ListView/colDefs";
import { ArchivedDevice } from "@services/devices/types";
import { ColDefWithTab, TabValue } from "../FilterColumn/context";
import ItemListButton from "@common/components/ListDetailViews/ListView/ItemListButton";
import Typography from "@common/components/Typography";
import { TFunction } from "react-i18next";
import ListTypography from "@common/components/ListDetailViews/ListView/ListTypography";

export type MappedArchivedDevice = Pick<
  MappedDevice,
  "id" | "deviceName" | "account" | "location" | "room"
> & {
  deletedBy: ArchivedDevice["deletedBy"];
  deletedTime: ArchivedDevice["deletedTime"];
  reason: ArchivedDevice["reason"];
};

export const LEFT_FIXED_SIZE = 320;
export const MIN_FIXED_SIZE = 120;

export const mapper = (d: ArchivedDevice): MappedArchivedDevice => {
  return {
    id: d.id,
    deviceName: d.name,
    account: d.room?.location?.account?.name ?? "",
    location: d.room?.location?.name ?? "",
    room: d.room?.name ?? "",
    deletedTime: d.deletedTime,
    reason: d.reason,
    deletedBy: d.deletedBy,
  };
};

export const sortingMapper = (key: string) => {
  switch (key) {
    case "account":
      return "room.location.account.name";
    case "location":
      return "room.location.name";
    case "room":
      return "room.name";
    case "deviceName":
      return "name";
    default:
      return key;
  }
};

const getValue = (value: string | number | undefined) =>
  Boolean(value) ? value : "-";

export const deviceInformations = (device?: ArchivedDevice) => {
  return [
    {
      title: "Archive",
      data: [
        {
          label: "Archive Date",
          value: getValue(device?.deletedTime),
        },
        { label: "Archived By", value: getValue(device?.deletedBy) },
        { label: "Archived Reason", value: getValue(device?.reason) },
      ],
    },
    {
      title: "General",
      data: [
        {
          label: "Device Name",
          value: getValue(device?.name),
        },
        {
          label: "Manufacturer",
          value: getValue(device?.manufacturer),
        },
        {
          label: "Category",
          value: getValue(device?.category),
        },
        {
          label: "Type",
          value: getValue(device?.type),
        },
        {
          label: "Model",
          value: getValue(device?.model),
        },
        {
          label: "Room",
          value: getValue(device?.room.name),
        },
        {
          label: "Location",
          value: getValue(device?.room?.location.name),
        },
        {
          label: "Account",
          value: getValue(device?.room?.location?.account.name),
        },
        {
          label: "Software Version",
          value: getValue(device?.softwareVersion),
        },
        {
          label: "Serial Number",
          value: getValue(device?.serialNumber),
        },
        {
          label: "MAC Address",
          value: getValue(device?.macAddress),
        },
        {
          label: "Management IP",
          value: getValue(device?.localIpAddress),
        },
      ],
    },
    {
      title: "Device Notes",
      data: [
        {
          label: undefined,
          value: device?.producerNotes,
        },
      ],
    },
  ];
};

export const getColumns = (
  t: TFunction<"translation", undefined>,
  getDeviceToShow?: (id: MappedArchivedDevice["id"]) => void
): ColDefWithTab<MappedArchivedDevice>[] => {
  return [
    {
      label: "Device Name",
      key: "deviceName",
      minWidth: LEFT_FIXED_SIZE,
      sortable: true,
      disabledToHide: true,
      tab: [TabValue.Archive],
      render: (row) => (
        <ItemListButton
          displayTicket={false}
          ticketColor={"available"}
          label={row.deviceName ?? ""}
          bookmark={false}
          previousState={t("Devices").toUpperCase()}
          onClick={() => getDeviceToShow && getDeviceToShow(row.id)}
        />
      ),
      renderHeader: (column) => (
        <Grid
          container
          sx={{ width: '100%', flexDirection: "row",
            justifyContent: "center",
            position: "relative", }}
        >
          <Typography
            size="small"
            sx={{
              textAlign: "center",
            }}
          >
            {t("Device Name")}
          </Typography>
        </Grid>
      ),
    },
    {
      label: "Account",
      key: "account",
      sortable: true,
      tab: [TabValue.Archive],
      minWidth: MIN_FIXED_SIZE,
      render: ({ account }) => (
        <ListTypography cellValue={account} tooltip={account} />
      ),
    },
    {
      label: "Location",
      key: "location",
      sortable: true,
      tab: [TabValue.Archive],
      minWidth: MIN_FIXED_SIZE,
      render: ({ location }) => (
        <ListTypography cellValue={location} tooltip={location} />
      ),
    },
    {
      label: "Room",
      key: "room",
      sortable: true,
      tab: [TabValue.Archive],
      minWidth: MIN_FIXED_SIZE,
      render: ({ room }) => <ListTypography cellValue={room} tooltip={room} />,
    },
    {
      label: "Archive Date",
      key: "deletedTime",
      sortable: true,
      tab: [TabValue.Archive],
      render: ({ deletedTime }) => (
        <ListTypography cellValue={displayDate(deletedTime)} />
      ),
      csvValue: ({ deletedTime }) => displayDateTimeCSV(deletedTime),
    },
    {
      label: "Archive By",
      key: "deletedBy",
      sortable: true,
      tab: [TabValue.Archive],
      minWidth: MIN_FIXED_SIZE,
      render: ({ deletedBy }) => (
        <ListTypography cellValue={deletedBy} tooltip={deletedBy} />
      ),
    },
    {
      label: "Archive Reason",
      key: "reason",
      sortable: true,
      tab: [TabValue.Archive],
      minWidth: MIN_FIXED_SIZE,
      render: ({ reason }) => (
        <ListTypography cellValue={reason} tooltip={reason} />
      ),
    },
  ];
};
